import LiftedTitleCard from "./LiftedTitleCard"
import React, { ReactNode } from "react"
import { cn } from "@/lib/utils"
import { miniStatComparisonCaptionForTimeframe } from "@/utils/dashboardDateWindowStorage"
import { resolveMiniStatGradient } from "@/utils/miniStatGradient"
import {
  dashboardMiniStatCardHeight,
  dashboardMiniStatCardHeightCompact,
} from "./liftedTitleCardUtils"

interface MiniStatisticsV2Props {
  title: string
  amount: string
  percentage: number | null
  chart?: ReactNode
  dateRange?: string
  amountColor?: string
  backgroundGradient?: string
  timeframe?: string
  onTimeframeChange?: (timeframe: string) => void
  tooltip?: string
  hideComparison?: boolean
  hideBottomSection?: boolean
  comparisonText?: string
  bottomMessage?: string
  height?: { base?: string; sm?: string; md?: string; lg?: string; xl?: string }
  titlePadding?: {
    pl?: { base?: string; sm?: string; md?: string; lg?: string; xl?: string }
    pr?: { base?: string; sm?: string; md?: string; lg?: string; xl?: string }
    py?: { base?: string; sm?: string; md?: string; lg?: string; xl?: string }
  }
  useNormalStyling?: boolean
}

const MiniStatisticsV2: React.FC<MiniStatisticsV2Props> = ({
  title,
  amount,
  percentage,
  amountColor,
  backgroundGradient,
  timeframe = "today",
  onTimeframeChange,
  tooltip,
  hideComparison = false,
  hideBottomSection = false,
  comparisonText,
  bottomMessage,
  height,
  titlePadding,
  useNormalStyling,
}) => {
  const autoGradient =
    useNormalStyling && hideComparison && percentage === null && !backgroundGradient
      ? undefined
      : resolveMiniStatGradient({ amount, percentage, backgroundGradient })

  const accentColor = amountColor ?? (autoGradient ? "white" : undefined)
  const valueColorClass = autoGradient
    ? "text-white"
    : accentColor
      ? undefined
      : "text-foreground"
  const valueColorStyle = !autoGradient && accentColor ? { color: accentColor } : undefined

  const comparisonCaption =
    comparisonText ?? miniStatComparisonCaptionForTimeframe(timeframe ?? "all-time")

  return (
    <LiftedTitleCard
      title={title}
      tooltip={tooltip}
      timeframe={timeframe}
      onTimeframeChange={onTimeframeChange}
      backgroundGradient={autoGradient}
      height={
        height ||
        (hideBottomSection && !useNormalStyling
          ? dashboardMiniStatCardHeightCompact
          : dashboardMiniStatCardHeight)
      }
      hideBottomSection={hideBottomSection}
      suppressMobileTitleBarFooter
      bottomMessage={bottomMessage}
      titlePadding={titlePadding}
      useNormalStyling={useNormalStyling}
    >
      <div
        data-dashboard-mini-stat-surface=""
        data-variant={autoGradient ? "gradient" : "muted"}
        className={cn(
          "relative m-0 flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden border-none",
          !autoGradient && "bg-muted"
        )}
        style={autoGradient ? { background: autoGradient } : undefined}
      >
        {autoGradient ? (
          <div
            className="pointer-events-none absolute inset-0 z-1"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
              `,
              backgroundSize: "40px 40px",
            }}
            aria-hidden
          />
        ) : null}

        <div
          className={cn(
            "relative z-2 m-0 flex min-h-0 min-w-0 flex-1 flex-col justify-start overflow-hidden p-0",
            hideBottomSection ? "pb-0" : "pb-3 sm:pb-5 md:pb-8 lg:pb-0"
          )}
        >
          <p
            className={cn(
              "m-0 max-w-full min-w-0 shrink-0 grow-0 overflow-hidden px-1.5 py-1.5 text-2xl leading-tight font-medium text-ellipsis whitespace-nowrap sm:py-2 sm:text-3xl md:text-4xl lg:text-5xl font-[family-name:'IBM_Plex_Sans',sans-serif]",
              valueColorClass
            )}
            style={valueColorStyle}
          >
            {amount}
          </p>
        </div>

        {!hideComparison ? (
          <div className="mt-auto w-full bg-transparent py-1 pr-2 pl-1.5 sm:py-2 sm:pr-2.5 sm:pl-2 md:py-2.5 md:pr-3 md:pl-2.5">
            <div
              data-dashboard-mini-stat-comparison-row=""
              className="flex items-center justify-start gap-x-1 gap-y-0 max-md:flex-nowrap sm:gap-x-1.5 md:gap-x-2"
            >
              <span
                data-dashboard-mini-stat-comparison-badge=""
                className={cn(
                  "inline-flex shrink-0 items-center rounded-[4px] border border-solid px-1.5 py-px text-[10px] font-medium leading-none sm:px-[7px] sm:py-0.5 sm:text-[11px] md:px-2 md:py-0.5 md:text-xs font-[family-name:'IBM_Plex_Sans',sans-serif]",
                  autoGradient
                    ? "border-white text-white"
                    : accentColor
                      ? "border-current"
                      : "border-foreground text-foreground"
                )}
                style={!autoGradient && accentColor ? { color: accentColor, borderColor: accentColor } : undefined}
              >
                {(() => {
                  const pct = percentage ?? 0
                  return `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`
                })()}
              </span>
              <span
                data-dashboard-mini-stat-comparison-caption=""
                className={cn(
                  "inline-flex min-w-0 items-center self-center leading-none font-[family-name:var(--font-primary)] text-[10px] font-medium sm:text-[11px] md:text-xs",
                  autoGradient
                    ? "text-white"
                    : accentColor
                      ? undefined
                      : "text-foreground",
                  comparisonText ? "normal-case" : "uppercase"
                )}
                style={!autoGradient && accentColor ? { color: accentColor } : undefined}
              >
                {comparisonCaption}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </LiftedTitleCard>
  )
}

export default MiniStatisticsV2
